import imageCompression from "browser-image-compression";

/**
 * Creates a compressed image for API data with fallback handling
 * @param {HTMLImageElement} image - The original image element
 * @param {Object} imgData - Image data configuration
 * @returns {Promise<Object>} Object containing finalImage, compressedImageObj, and uncompressedImage
 */
export const createCompressedImageForApiData = async (image, imgData) => {
  if (imgData.compress !== false) {
    // Compression is enabled, create compressed version with 50% quality
    try {
      const compressedImageObj = await compressImage(image, 0.5);
      return {
        finalImage: compressedImageObj,
        compressedImageObj: compressedImageObj,
        uncompressedImage: image,
      };
    } catch (error) {
      console.error("Failed to compress image:", error);
      return {
        finalImage: image,
        compressedImageObj: null,
        uncompressedImage: image,
      };
    }
  } else {
    return {
      finalImage: image,
      compressedImageObj: null,
      uncompressedImage: image,
    };
  }
};

// Max dimension for canvas display — keep low for snappy interaction.
const DISPLAY_MAX_DIMENSION = 1024;
// Pixel count above which we always compress (e.g. 1080p+).
const LARGE_PIXEL_COUNT = 1024 * 1024;

/**
 * Whether resized/canvas-encoded output should preserve alpha (PNG) vs JPEG.
 * Blob/object URLs are treated as possibly transparent (e.g. pasted PNG).
 */
const shouldPreserveAlphaForSrc = (src) => {
  if (!src || typeof src !== "string") return true;
  if (src.startsWith("data:")) {
    return !/^data:image\/(jpeg|jpg)(;|,)/i.test(src);
  }
  if (/\.jpe?g(\?|#|$)/i.test(src)) return false;
  return true;
};

/**
 * Compresses an image if it exceeds specified dimensions or pixel count.
 * Uses lower defaults (1200px) so 30MB+ images don't slow the canvas.
 * @param {HTMLImageElement} image - The image to potentially compress
 * @param {number} maxWidth - Maximum width (default: 1200)
 * @param {number} maxHeight - Maximum height (default: 1200)
 * @param {number} quality - Compression quality (default: 0.72 for speed)
 * @returns {string} Compressed image data URL or original image src
 */
export const compressImageIfNeeded = (
  image,
  maxWidth = DISPLAY_MAX_DIMENSION,
  maxHeight = DISPLAY_MAX_DIMENSION,
  quality = 0.72
) => {
  try {
    const canvas = document.createElement("canvas");
    let width = image.naturalWidth || image.width;
    let height = image.naturalHeight || image.height;

    const pixelCount = width * height;
    const needsResize =
      width > maxWidth ||
      height > maxHeight ||
      pixelCount > LARGE_PIXEL_COUNT;

    if (!needsResize) {
      return image.src; // Return original if small enough
    }

    // Calculate new dimensions maintaining aspect ratio
    const aspectRatio = width / height;
    if (width > height) {
      width = maxWidth;
      height = Math.round(maxWidth / aspectRatio);
    } else {
      height = maxHeight;
      width = Math.round(maxHeight * aspectRatio);
    }

    canvas.width = width;
    canvas.height = height;

    const preserveAlpha = shouldPreserveAlphaForSrc(image.src);
    const ctx = canvas.getContext("2d", { alpha: preserveAlpha });
    ctx.drawImage(image, 0, 0, width, height);

    return preserveAlpha
      ? canvas.toDataURL("image/png")
      : canvas.toDataURL("image/jpeg", quality);
  } catch (error) {
    console.error("Error compressing image:", error);
    return image.src; // Return original on error
  }
};

// Max dimension for initial placement scale (used for default width/height only).
const PLACEMENT_MAX_DIMENSION = 300;
// Max dimension for the image we store in canvas state — 1024 keeps redraws fast.
const CANVAS_DISPLAY_MAX_DIMENSION = 1024;

/**
 * Creates a downscaled HTMLImageElement for display when the source is very large.
 * Caller must revoke the blob URL when done if they created it.
 */
function createDownscaledDisplayImage(image, maxDim, quality = 0.72, srcHint) {
  return new Promise((resolve, reject) => {
    try {
      const w = image.naturalWidth || image.width;
      const h = image.naturalHeight || image.height;
      if (w <= maxDim && h <= maxDim) {
        resolve(image);
        return;
      }
      const scale = Math.min(maxDim / w, maxDim / h);
      const width = Math.round(w * scale);
      const height = Math.round(h * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const preserveAlpha = shouldPreserveAlphaForSrc(srcHint || image.src);
      const ctx = canvas.getContext("2d", { alpha: preserveAlpha });
      ctx.drawImage(image, 0, 0, width, height);
      const dataUrl = preserveAlpha
        ? canvas.toDataURL("image/png")
        : canvas.toDataURL("image/jpeg", quality);
      const downscaled = new Image();
      downscaled.onload = () => {
        canvas.width = 0;
        canvas.height = 0;
        resolve(downscaled);
      };
      downscaled.onerror = () => {
        canvas.width = 0;
        canvas.height = 0;
        resolve(image);
      };
      downscaled.src = dataUrl;
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Processes and compresses an image for canvas display.
 * For large images (e.g. 30MB+), creates a downscaled display copy so the canvas stays responsive.
 * @param {HTMLImageElement} image - The image to process
 * @param {string} src - The image source URL
 * @returns {Promise<Object>} Processed image data
 */
export const processCompressedImage = async (image, src) => {
  const originalWidth = image.naturalWidth;
  const originalHeight = image.naturalHeight;

  const maxDimension = PLACEMENT_MAX_DIMENSION;
  let scaledWidth = originalWidth;
  let scaledHeight = originalHeight;

  if (originalWidth > maxDimension || originalHeight > maxDimension) {
    const scaleFactor = Math.min(
      maxDimension / originalWidth,
      maxDimension / originalHeight
    );
    scaledWidth = originalWidth * scaleFactor;
    scaledHeight = originalHeight * scaleFactor;
  }

  // For large images: use a downscaled version for canvas display (snappier redraws).
  const isLarge =
    originalWidth > CANVAS_DISPLAY_MAX_DIMENSION ||
    originalHeight > CANVAS_DISPLAY_MAX_DIMENSION ||
    (originalWidth * originalHeight) > LARGE_PIXEL_COUNT;

  let displayImage = image;
  if (isLarge) {
    try {
      displayImage = await createDownscaledDisplayImage(
        image,
        CANVAS_DISPLAY_MAX_DIMENSION,
        0.72,
        src
      );
    } catch (err) {
      console.warn("Downscale for display failed, using original:", err);
    }
  }

  return {
    compressedImage: displayImage,
    scaledWidth,
    scaledHeight,
    originalWidth,
    originalHeight,
  };
};

/**
 * Compresses an image using canvas toDataURL (faster than browser-image-compression)
 * @param {HTMLImageElement} originalImage - The original image element
 * @param {number} quality - Compression quality (default: 0.4)
 * @returns {Promise<HTMLImageElement>} Compressed image element
 */
export const compressImage = async (originalImage, quality = 0.4) => {
  return new Promise((resolve) => {
    try {
      // PERFORMANCE FIX: Use simple canvas compression instead of browser-image-compression
      // This is much faster and doesn't block the UI thread
      const canvas = document.createElement("canvas");
      const maxDimension = 1200; // Reduce max dimension for better performance

      let width = originalImage.width || originalImage.naturalWidth;
      let height = originalImage.height || originalImage.naturalHeight;

      // Scale down if too large
      if (width > maxDimension || height > maxDimension) {
        const scale = Math.min(maxDimension / width, maxDimension / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }

      canvas.width = width;
      canvas.height = height;

      const preserveAlpha = shouldPreserveAlphaForSrc(originalImage.src);
      const ctx = canvas.getContext("2d", { alpha: preserveAlpha });
      ctx.drawImage(originalImage, 0, 0, width, height);

      const compressedDataUrl = preserveAlpha
        ? canvas.toDataURL("image/png")
        : canvas.toDataURL("image/jpeg", quality);

      const compressed = new Image();
      compressed.onload = () => {
        // Clean up canvas to free memory
        canvas.width = 0;
        canvas.height = 0;
        resolve(compressed);
      };
      compressed.onerror = () => {
        // Clean up canvas
        canvas.width = 0;
        canvas.height = 0;
        // Fallback to original if compression fails
        resolve(originalImage);
      };
      compressed.src = compressedDataUrl;
    } catch (error) {
      console.error("Compression error:", error);
      // Fallback to original image if compression fails
      resolve(originalImage);
    }
  });
};
